// DropOffFunnel — honest attrition story for the FORGE pipeline.
//
// Replaces the waterfall's implicit "BOD is biggest loss" artifact with the
// actual story from dash_applicants: ~55% of applicants drop before AIS.
// BOD declines are <5%. Physical failures are the next-biggest gate after AIS
// non-attendance.
//
// Extensions over the first pass:
//   - Persona correlation (Zoe / Michael / Xavier / Barbara) via member DOB lookup
//   - Monthly cohort trend (last 24 months) — entries vs. outcomes
//   - Explicit persona-coverage note so leaders know what the Unknown cohort means
//
// Source: GET /api/forge/drop-off (src/worker/forge/drop-off-api.ts).

import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type Persona = "zoe" | "michael" | "xavier" | "barbara" | "unknown";
type Bucket =
  | "bod_approved"
  | "bod_declined"
  | "physical_failure"
  | "ais_no_show"
  | "dropped_before_ais"
  | "withdrawn_archived"
  | "in_process";

interface DropOffStage {
  key: string;
  label: string;
  count: number;
  descriptor: string;
}

interface PersonaBucketRow {
  persona: Persona;
  label: string;
  total: number;
  buckets: Record<Bucket, number>;
  approval_rate: number | null;
}

interface MonthlyCohort {
  month: string;
  entered: number;
  buckets: Record<Bucket, number>;
}

interface DropOffResponse {
  total: number;
  computed_at: string;
  stages: DropOffStage[];
  accounting: {
    total: number;
    in_process: number;
    dropped_before_ais: number;
    ais_no_show: number;
    physical_failure: number;
    bod_declined: number;
    bod_approved: number;
    withdrawn_archived: number;
    unknown: number;
  };
  monthly_excluded_bad_date: number;
  by_persona: PersonaBucketRow[];
  persona_coverage: {
    classified: number;
    unknown: number;
    classified_pct: number;
    note: string;
  };
  monthly: MonthlyCohort[];
  sanity: { sum_of_buckets: number; matches_total: boolean };
}

const PERSONA_COLOR: Record<Persona, string> = {
  zoe: "#8B5CF6",
  michael: "#2563EB",
  xavier: "#0E403A",
  barbara: "#D97706",
  unknown: "#6B7280",
};

function useDropOff(): DropOffResponse | null {
  const [data, setData] = useState<DropOffResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/forge/drop-off", { credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<DropOffResponse>) : null))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return data;
}

function pct(num: number, den: number): string {
  if (den <= 0) return "—";
  return `${((num / den) * 100).toFixed(1)}%`;
}

export default function DropOffFunnel() {
  const data = useDropOff();
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        Loading drop-off analysis…
      </div>
    );
  }

  const total = data.total;
  const a = data.accounting;

  const lossBuckets = [
    {
      key: "dropped_before_ais",
      label: "Dropped before AIS",
      count: a.dropped_before_ais,
    },
    { key: "ais_no_show", label: "AIS no-show", count: a.ais_no_show },
    {
      key: "physical_failure",
      label: "Physical failure",
      count: a.physical_failure,
    },
    { key: "bod_declined", label: "BOD declined", count: a.bod_declined },
  ];
  lossBuckets.sort((x, y) => y.count - x.count);
  const biggestLossKey = lossBuckets[0]?.key ?? null;

  const approvedPct = pct(a.bod_approved, total);
  const biggestLoss = lossBuckets[0];
  const biggestLossPct = pct(biggestLoss.count, total);

  // Monthly chart data — stack entered vs approved vs dropped_before_ais
  const monthlyChart = data.monthly.map((m) => ({
    month: m.month,
    entered: m.entered,
    approved: m.buckets.bod_approved,
    dropped_before_ais: m.buckets.dropped_before_ais,
    ais_no_show: m.buckets.ais_no_show,
    phys_fail: m.buckets.physical_failure,
    bod_declined: m.buckets.bod_declined,
    withdrawn: m.buckets.withdrawn_archived,
    in_process: m.buckets.in_process,
  }));

  return (
    <div className="glass rounded-2xl p-5 md:p-6">
      <header className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
            Where Applicants Are Lost
          </p>
          <h3 className="text-lg font-bold text-theme-primary tracking-tight">
            {total.toLocaleString()} applicants →{" "}
            {a.bod_approved.toLocaleString()} voted in ({approvedPct})
          </h3>
          <p className="text-sm text-theme-secondary mt-1">
            The real funnel. BOD declines are rare ({a.bod_declined} total,{" "}
            {pct(a.bod_declined, total)} of applicants) — the biggest loss is{" "}
            <strong>{biggestLoss.label.toLowerCase()}</strong> at{" "}
            {biggestLossPct}.
          </p>
          {!data.sanity.matches_total && (
            <p className="text-xs text-amber-600 mt-1">
              Sanity: buckets sum to {data.sanity.sum_of_buckets} of {total}{" "}
              applicants — {total - data.sanity.sum_of_buckets} unclassified.
            </p>
          )}
        </div>
      </header>

      {/* Visual funnel: horizontal bars sized by count relative to total */}
      <div className="space-y-2 mb-6">
        {data.stages.map((s) => {
          const isTotal = s.key === "total";
          const isApproved = s.key === "bod-approved";
          const isBiggestLoss =
            (biggestLossKey === "dropped_before_ais" && s.key === "no-ais") ||
            (biggestLossKey === "ais_no_show" && s.key === "ais-no-show") ||
            (biggestLossKey === "physical_failure" &&
              s.key === "physical-failure") ||
            (biggestLossKey === "bod_declined" && s.key === "bod-declined");
          const barColor = isTotal
            ? "bg-theme-secondary/30"
            : isApproved
              ? "bg-emerald-500/70"
              : isBiggestLoss
                ? "bg-red-500/70"
                : "bg-theme-overlay border border-theme-subtle";
          const widthPct =
            total > 0 ? Math.max(3, Math.min(100, (s.count / total) * 100)) : 0;
          return (
            <div key={s.key} className="flex items-center gap-3 text-sm">
              <div className="w-44 md:w-56 flex-shrink-0 text-theme-secondary font-medium">
                {s.label}
              </div>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <div className="flex-1 rounded-md h-6 bg-theme-overlay/30 overflow-hidden relative">
                  <div
                    className={`h-full rounded-md ${barColor} transition-all`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                <div className="w-28 flex-shrink-0 text-right">
                  <span className="font-mono tabular-nums text-theme-primary font-semibold">
                    {s.count.toLocaleString()}
                  </span>
                  {!isTotal && (
                    <span className="ml-2 text-xs text-theme-muted tabular-nums">
                      {pct(s.count, total)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Persona breakdown */}
      <section className="mt-6 pt-4 border-t border-theme-subtle">
        <header className="mb-3">
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
            Persona correlation
          </p>
          <p className="text-sm text-theme-secondary">
            Persona is inferred from DOB on the matching{" "}
            <code className="font-mono">dash_members</code> row — applicants who
            never became members stay in <strong>Unknown</strong>. Coverage:{" "}
            {data.persona_coverage.classified_pct.toFixed(1)}% of applicants
            classified ({data.persona_coverage.classified.toLocaleString()} of{" "}
            {total.toLocaleString()}).
          </p>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                <th className="py-2 pr-3 font-medium">Persona</th>
                <th className="py-2 px-3 font-medium text-right">Total</th>
                <th className="py-2 px-3 font-medium text-right">
                  Dropped before AIS
                </th>
                <th className="py-2 px-3 font-medium text-right">
                  AIS no-show
                </th>
                <th className="py-2 px-3 font-medium text-right">Phys fail</th>
                <th className="py-2 px-3 font-medium text-right">
                  BOD declined
                </th>
                <th className="py-2 px-3 font-medium text-right">Withdrawn</th>
                <th className="py-2 px-3 font-medium text-right">
                  BOD approved
                </th>
                <th className="py-2 px-3 font-medium text-right">In process</th>
                <th className="py-2 pl-3 font-medium text-right">Approval</th>
              </tr>
            </thead>
            <tbody>
              {data.by_persona.map((p) => (
                <tr
                  key={p.persona}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: PERSONA_COLOR[p.persona] }}
                      />
                      <span className="text-theme-primary font-medium">
                        {p.label}
                      </span>
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-primary">
                    {p.total.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.dropped_before_ais.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.ais_no_show.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.physical_failure.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.bod_declined.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.withdrawn_archived.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-emerald-600 font-semibold">
                    {p.buckets.bod_approved.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                    {p.buckets.in_process.toLocaleString()}
                  </td>
                  <td className="py-2 pl-3 text-right font-mono tabular-nums text-theme-primary">
                    {p.approval_rate === null
                      ? "—"
                      : `${(p.approval_rate * 100).toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-theme-muted mt-3 italic">
          {data.persona_coverage.note}
        </p>
      </section>

      {/* Monthly cohort trend */}
      {monthlyChart.length > 0 && (
        <section className="mt-6 pt-4 border-t border-theme-subtle">
          <header className="mb-3">
            <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
              Cohort trend — last 24 months
            </p>
            <p className="text-sm text-theme-secondary">
              Each bar is an intake cohort bucketed by the applicant's created
              month. The stack shows how that month's applicants ultimately
              landed — approvals, drops, still in process.
            </p>
            {data.monthly_excluded_bad_date > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {data.monthly_excluded_bad_date.toLocaleString()} applicant
                {data.monthly_excluded_bad_date === 1 ? "" : "s"} excluded from
                this chart (Created date unparseable) — still counted in the
                global totals above.
              </p>
            )}
          </header>

          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart
                data={monthlyChart}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  stroke="#e5e7eb"
                  strokeDasharray="3 3"
                  opacity={0.4}
                />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="dropped_before_ais"
                  stackId="outcome"
                  fill="#ef4444"
                  name="Dropped before AIS"
                />
                <Bar
                  dataKey="ais_no_show"
                  stackId="outcome"
                  fill="#f97316"
                  name="AIS no-show"
                />
                <Bar
                  dataKey="phys_fail"
                  stackId="outcome"
                  fill="#f59e0b"
                  name="Phys fail"
                />
                <Bar
                  dataKey="bod_declined"
                  stackId="outcome"
                  fill="#a16207"
                  name="BOD declined"
                />
                <Bar
                  dataKey="withdrawn"
                  stackId="outcome"
                  fill="#9ca3af"
                  name="Withdrawn/archived"
                />
                <Bar
                  dataKey="in_process"
                  stackId="outcome"
                  fill="#6b7280"
                  name="In process"
                />
                <Bar
                  dataKey="approved"
                  stackId="outcome"
                  fill="#10b981"
                  name="BOD approved"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Narrative footer */}
      <footer className="mt-6 pt-4 border-t border-theme-subtle text-xs text-theme-muted space-y-1">
        <p>
          <strong className="text-theme-secondary">How to read this:</strong>{" "}
          every number is sourced live from
          <code className="mx-1 font-mono">dash_applicants</code> — bucketed by{" "}
          <code className="font-mono">AIS_Status</code> and{" "}
          <code className="font-mono">Application_Result</code>. Persona joins
          to <code className="font-mono">dash_members</code> via email/name to
          borrow DOB.
        </p>
        <p>
          The board historically declines only 1-3 applicants per year. The
          waterfall above was conflating "no BOD outcome recorded" with "BOD
          declined" — this section shows where the real losses happen.
        </p>
      </footer>
    </div>
  );
}
